import React from 'react';
import {defineMessages, useIntl} from 'react-intl';
import type {ChipValue} from '@igp/shared/components/chip';
import {
    type ChipColumn,
    type DataGridColumn,
    DataGridServer,
    type DataGridServerProps,
    type TextColumn,
} from '@igp/shared/components/data-grid';
import {useDefaultSymbol} from '@igp/shared/components/label';
import {formatTimestamp} from '@igp/shared/utils';

import type {IsPro} from '@features/player-level-report-v2/types';

import type {PlayerEcologyWeeklyColumns, PlayerEcologyWeeklyViewModel} from '../types';

export const localizedHeaders = defineMessages<PlayerEcologyWeeklyColumns>({
    playerUid: {
        id: 'PlayerEcologyWeeklyDataGrid_playerUid',
        defaultMessage: 'Player ID',
    },
    playerUsername: {
        id: 'PlayerEcologyWeeklyDataGrid_playerUsername',
        defaultMessage: 'Player Username',
    },
    agentUid: {
        id: 'PlayerEcologyWeeklyDataGrid_agentUid',
        defaultMessage: 'Agent ID',
    },
    agentUsername: {
        id: 'PlayerEcologyWeeklyDataGrid_agentUsername',
        defaultMessage: 'Agent Username',
    },
    country: {
        id: 'PlayerEcologyWeeklyDataGrid_country',
        defaultMessage: 'Country',
    },
    'reportWeekStart.seconds': {
        id: 'PlayerEcologyWeeklyDataGrid_reportWeekStart.seconds',
        defaultMessage: 'Report Week Start',
    },
    'ecologySummary.ecology_flag': {
        id: 'PlayerEcologyWeeklyDataGrid_ecologySummary.ecology_flag',
        defaultMessage: 'Ecology Flag',
    },
    'ecologySummary.is_pro_shortdeck': {
        id: 'PlayerEcologyWeeklyDataGrid_ecologySummary.is_pro_shortdeck',
        defaultMessage: 'Shortdeck PRO',
    },
    'ecologySummary.is_pro_zoom': {
        id: 'PlayerEcologyWeeklyDataGrid_ecologySummary.is_pro_zoom',
        defaultMessage: 'Zoom PRO',
    },
    'ecologySummary.stake_2_is_pro_nlhe': {
        id: 'PlayerEcologyWeeklyDataGrid_ecologySummary.stake_2_is_pro_nlhe',
        defaultMessage: 'NLHE $2 PRO',
    },
    'ecologySummary.stake_4_is_pro_nlhe': {
        id: 'PlayerEcologyWeeklyDataGrid_ecologySummary.stake_4_is_pro_nlhe',
        defaultMessage: 'NLHE $4 PRO',
    },
    'ecologySummary.stake_10_is_pro_nlhe': {
        id: 'PlayerEcologyWeeklyDataGrid_ecologySummary.stake_10_is_pro_nlhe',
        defaultMessage: 'NLHE $10 PRO',
    },
    'ecologySummary.stake_20_is_pro_nlhe': {
        id: 'PlayerEcologyWeeklyDataGrid_ecologySummary.stake_20_is_pro_nlhe',
        defaultMessage: 'NLHE $20 PRO',
    },
    'ecologySummary.stake_50_is_pro_nlhe': {
        id: 'PlayerEcologyWeeklyDataGrid_ecologySummary.stake_50_is_pro_nlhe',
        defaultMessage: 'NLHE $50 PRO',
    },
    'ecologySummary.stake_100_is_pro_nlhe': {
        id: 'PlayerEcologyWeeklyDataGrid_ecologySummary.stake_100_is_pro_nlhe',
        defaultMessage: 'NLHE $100 PRO',
    },
    'ecologySummary.stake_200_is_pro_nlhe': {
        id: 'PlayerEcologyWeeklyDataGrid_ecologySummary.stake_200_is_pro_nlhe',
        defaultMessage: 'NLHE $200 PRO',
    },
    'ecologySummary.stake_400_is_pro_nlhe': {
        id: 'PlayerEcologyWeeklyDataGrid_ecologySummary.stake_400_is_pro_nlhe',
        defaultMessage: 'NLHE $400 PRO',
    },
});

const localized = defineMessages<IsPro>({
    Pro: {
        id: 'PlayerEcologyWeeklyDataGrid_Pro',
        defaultMessage: 'Pro',
    },
    NonPro: {
        id: 'PlayerEcologyWeeklyDataGrid_NonPro',
        defaultMessage: 'Non-Pro',
    },
    Mixed: {
        id: 'PlayerEcologyWeeklyDataGrid_Mixed',
        defaultMessage: 'Mixed',
    },
});

export function usePlayerEcologyWeeklyColumns(columns: PlayerEcologyWeeklyColumns[]): DataGridColumn<PlayerEcologyWeeklyViewModel>[] {
    const {formatMessage} = useIntl();
    const emptyStakeSymbol = useDefaultSymbol('N/A');
    const playerUid: TextColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: row => row.playerUid,
        title: localizedHeaders.playerUid,
        sortable: true,
        field: 'playerUid',
    };
    const playerUsername: TextColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: row => row.playerUsername,
        title: localizedHeaders.playerUsername,
        sortable: true,
        field: 'playerUsername',
    };
    const agentUid: TextColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: row => row.agentUid,
        title: localizedHeaders.agentUid,
        sortable: true,
        field: 'agentUid',
    };
    const agentUsername: TextColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: row => row.agentUsername,
        title: localizedHeaders.agentUsername,
        sortable: true,
        field: 'agentUsername',
    };
    const country: TextColumn<PlayerEcologyWeeklyViewModel> = {
        width: 100,
        type: 'text',
        valueGetter: row => row.country,
        title: localizedHeaders.country,
        sortable: true,
        field: 'country',
    };
    const reportWeekStart: TextColumn<PlayerEcologyWeeklyViewModel> = {
        width: 180,
        type: 'text',
        valueGetter: row => formatTimestamp(row.reportWeekStart, 'date'),
        title: localizedHeaders['reportWeekStart.seconds'],
        sortable: true,
        field: 'reportWeekStart.seconds',
    };
    const ecology_flag: TextColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'text',
        valueGetter: row => row.ecologySummary.ecology_flag,
        title: localizedHeaders['ecologySummary.ecology_flag'],
        sortable: true,
        field: 'ecologySummary.ecology_flag',
    };
    const is_pro_shortdeck: ChipColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'chip',
        valueGetter: row => getStakeChipValue(row.ecologySummary.is_pro_shortdeck),
        title: localizedHeaders['ecologySummary.is_pro_shortdeck'],
        sortable: true,
        field: 'ecologySummary.is_pro_shortdeck',
    };
    const is_pro_zoom: ChipColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'chip',
        valueGetter: row => getStakeChipValue(row.ecologySummary.is_pro_zoom),
        title: localizedHeaders['ecologySummary.is_pro_zoom'],
        sortable: true,
        field: 'ecologySummary.is_pro_zoom',
    };
    const stake_2_is_pro_nlhe: ChipColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'chip',
        valueGetter: row => getStakeChipValue(row.ecologySummary.stake_2_is_pro_nlhe),
        title: localizedHeaders['ecologySummary.stake_2_is_pro_nlhe'],
        sortable: true,
        field: 'ecologySummary.stake_2_is_pro_nlhe',
        emptySymbol: emptyStakeSymbol,
    };
    const stake_4_is_pro_nlhe: ChipColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'chip',
        valueGetter: row => getStakeChipValue(row.ecologySummary.stake_4_is_pro_nlhe),
        title: localizedHeaders['ecologySummary.stake_4_is_pro_nlhe'],
        sortable: true,
        field: 'ecologySummary.stake_4_is_pro_nlhe',
        emptySymbol: emptyStakeSymbol,
    };
    const stake_10_is_pro_nlhe: ChipColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'chip',
        valueGetter: row => getStakeChipValue(row.ecologySummary.stake_10_is_pro_nlhe),
        title: localizedHeaders['ecologySummary.stake_10_is_pro_nlhe'],
        sortable: true,
        field: 'ecologySummary.stake_10_is_pro_nlhe',
        emptySymbol: emptyStakeSymbol,
    };
    const stake_20_is_pro_nlhe: ChipColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'chip',
        valueGetter: row => getStakeChipValue(row.ecologySummary.stake_20_is_pro_nlhe),
        title: localizedHeaders['ecologySummary.stake_20_is_pro_nlhe'],
        sortable: true,
        field: 'ecologySummary.stake_20_is_pro_nlhe',
        emptySymbol: emptyStakeSymbol,
    };
    const stake_50_is_pro_nlhe: ChipColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'chip',
        valueGetter: row => getStakeChipValue(row.ecologySummary.stake_50_is_pro_nlhe),
        title: localizedHeaders['ecologySummary.stake_50_is_pro_nlhe'],
        sortable: true,
        field: 'ecologySummary.stake_50_is_pro_nlhe',
        emptySymbol: emptyStakeSymbol,
    };
    const stake_100_is_pro_nlhe: ChipColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'chip',
        valueGetter: row => getStakeChipValue(row.ecologySummary.stake_100_is_pro_nlhe),
        title: localizedHeaders['ecologySummary.stake_100_is_pro_nlhe'],
        sortable: true,
        field: 'ecologySummary.stake_100_is_pro_nlhe',
        emptySymbol: emptyStakeSymbol,
    };
    const stake_200_is_pro_nlhe: ChipColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'chip',
        valueGetter: row => getStakeChipValue(row.ecologySummary.stake_200_is_pro_nlhe),
        title: localizedHeaders['ecologySummary.stake_200_is_pro_nlhe'],
        sortable: true,
        field: 'ecologySummary.stake_200_is_pro_nlhe',
        emptySymbol: emptyStakeSymbol,
    };
    const stake_400_is_pro_nlhe: ChipColumn<PlayerEcologyWeeklyViewModel> = {
        width: 150,
        type: 'chip',
        valueGetter: row => getStakeChipValue(row.ecologySummary.stake_400_is_pro_nlhe),
        title: localizedHeaders['ecologySummary.stake_400_is_pro_nlhe'],
        sortable: true,
        field: 'ecologySummary.stake_400_is_pro_nlhe',
        emptySymbol: emptyStakeSymbol,
    };
    const config: Record<PlayerEcologyWeeklyColumns, DataGridColumn<PlayerEcologyWeeklyViewModel>> = {
        playerUid,
        playerUsername,
        agentUid,
        agentUsername,
        country,
        'reportWeekStart.seconds': reportWeekStart,
        'ecologySummary.ecology_flag': ecology_flag,
        'ecologySummary.is_pro_shortdeck': is_pro_shortdeck,
        'ecologySummary.is_pro_zoom': is_pro_zoom,
        'ecologySummary.stake_2_is_pro_nlhe': stake_2_is_pro_nlhe,
        'ecologySummary.stake_4_is_pro_nlhe': stake_4_is_pro_nlhe,
        'ecologySummary.stake_10_is_pro_nlhe': stake_10_is_pro_nlhe,
        'ecologySummary.stake_20_is_pro_nlhe': stake_20_is_pro_nlhe,
        'ecologySummary.stake_50_is_pro_nlhe': stake_50_is_pro_nlhe,
        'ecologySummary.stake_100_is_pro_nlhe': stake_100_is_pro_nlhe,
        'ecologySummary.stake_200_is_pro_nlhe': stake_200_is_pro_nlhe,
        'ecologySummary.stake_400_is_pro_nlhe': stake_400_is_pro_nlhe,
    };

    function getStakeChipValue(value: IsPro): ChipValue {
        const localizedValue = localized[value] ? formatMessage(localized[value]) : undefined;
        return {value: localizedValue ?? value, color: 'grey', type: 'round'};
    }

    return columns.map(column => config[column]).filter(i => i);
}

type PlayerEcologyWeeklyDataGridProps = Omit<DataGridServerProps<PlayerEcologyWeeklyViewModel, PlayerEcologyWeeklyColumns>, 'columns'> & {
    columns: PlayerEcologyWeeklyColumns[];
};

export function PlayerEcologyWeeklyDataGrid({columns, ...props}: PlayerEcologyWeeklyDataGridProps) {
    const columnsConfigs = usePlayerEcologyWeeklyColumns(columns);

    return <DataGridServer {...props} columns={columnsConfigs} />;
}
